import { Injectable, signal, inject } from '@angular/core';
import {
  FeedbackService,
  FeedbackEntityTypeConfig,
  FeedbackType,
  FeedbackCategory,
} from './api/yuload/feedback.service';

export interface FeedbackModalConfig {
  entityType: string;
  entityId: string;
  entityName?: string;
  defaultFeedbackType?: FeedbackType;
  allowedCategories?: string[];
  metadata?: Record<string, any>;
}

export interface FeedbackModalState {
  isOpen: boolean;
  config: FeedbackModalConfig | null;
  loading: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class FeedbackModalService {
  private readonly feedbackService = inject(FeedbackService);

  private readonly _state = signal<FeedbackModalState>({
    isOpen: false,
    config: null,
    loading: false,
  });

  // Cache for entity types
  private entityTypesCache = signal<FeedbackEntityTypeConfig[]>([]);
  private entityTypesCacheLoaded = false;

  readonly state = this._state.asReadonly();

  /**
   * Load entity types from API and cache them
   */
  private loadEntityTypes(): Promise<FeedbackEntityTypeConfig[]> {
    if (this.entityTypesCacheLoaded && this.entityTypesCache().length > 0) {
      return Promise.resolve(this.entityTypesCache());
    }

    return new Promise((resolve, reject) => {
      this.feedbackService.getEntityTypes().subscribe({
        next: (entityTypes) => {
          this.entityTypesCache.set(entityTypes);
          this.entityTypesCacheLoaded = true;
          resolve(entityTypes);
        },
        error: (error) => {
          console.error('Error loading entity types:', error);
          // Return empty array on error, will use fallback categories
          resolve([]);
        },
      });
    });
  }

  /**
   * Get categories for a specific entity type
   */
  private getCategoriesForEntityType(entityType: string): string[] {
    const entityTypeConfig = this.entityTypesCache().find((et) => et.id === entityType);
    return entityTypeConfig?.categories || [];
  }

  /**
   * Opens the feedback modal with the given configuration
   * Fetches categories from entity types API
   */
  async open(config: Omit<FeedbackModalConfig, 'allowedCategories'>): Promise<void> {
    this._state.update((state) => ({ ...state, loading: true }));

    try {
      await this.loadEntityTypes();
      const categories = this.getCategoriesForEntityType(config.entityType);

      this._state.set({
        isOpen: true,
        config: {
          ...config,
          allowedCategories: categories,
        },
        loading: false,
      });
    } catch (error) {
      console.error('Error opening feedback modal:', error);
      // Open modal without categories on error
      this._state.set({
        isOpen: true,
        config: {
          ...config,
          allowedCategories: [],
        },
        loading: false,
      });
    }
  }

  /**
   * Closes the feedback modal
   */
  close(): void {
    this._state.set({
      isOpen: false,
      config: null,
      loading: false,
    });
  }

  /**
   * Clears the entity types cache (useful when entity types are updated)
   */
  clearCache(): void {
    this.entityTypesCache.set([]);
    this.entityTypesCacheLoaded = false;
  }

  /**
   * Convenience method to open feedback for a purchase format
   */
  openForPurchaseFormat(
    formatId: string,
    formatName?: string,
    metadata?: Record<string, any>,
    defaultFeedbackType?: FeedbackType,
  ): void {
    this.open({
      entityType: 'extracted-purchase-format',
      entityId: formatId,
      entityName: formatName,
      defaultFeedbackType,
      metadata,
    });
  }

  /**
   * Convenience method to open feedback for a load
   */
  openForLoad(loadId: string, loadName?: string, metadata?: Record<string, any>): void {
    this.open({
      entityType: 'load',
      entityId: loadId,
      entityName: loadName,
      metadata,
    });
  }

  /**
   * Convenience method to open feedback for an extracted product
   */
  openForExtractedProduct(productId: string, productName?: string, metadata?: Record<string, any>): void {
    this.open({
      entityType: 'extracted-product',
      entityId: productId,
      entityName: productName,
      metadata,
    });
  }

  /**
   * Convenience method to open feedback for an extracted supplier
   */
  openForExtractedSupplier(supplierId: string, supplierName?: string, metadata?: Record<string, any>): void {
    this.open({
      entityType: 'extracted-supplier',
      entityId: supplierId,
      entityName: supplierName,
      metadata,
    });
  }

  /**
   * Convenience method to open feedback for a document
   */
  openForDocument(documentId: string, documentName?: string, metadata?: Record<string, any>): void {
    this.open({
      entityType: 'load-document',
      entityId: documentId,
      entityName: documentName,
      metadata,
    });
  }
}
